'use client';

import { Check, ChevronDown, Search } from 'lucide-react';
import {
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';

export interface CountryComboboxOption {
  /** The code committed to the parent (e.g. an ISO country code or 'XX'). */
  value: string;
  /** Localized text shown in the trigger and the list. */
  label: string;
  /**
   * Extra match terms beyond `label` — e.g. the English name and the ISO code,
   * so a Slovak user can still find 'Švajčiarsko' by typing "Switzerland" or
   * "CH". Matching is diacritic- and case-insensitive on label + keywords.
   */
  keywords?: string[];
}

interface CountryComboboxProps {
  options: CountryComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Search input placeholder inside the dropdown. */
  searchPlaceholder?: string;
  /** Shown when the query matches no option. */
  emptyText?: string;
  /** className for the trigger button (callers set width / bg here). */
  className?: string | undefined;
  error?: boolean;
  'aria-label'?: string | undefined;
  /**
   * id forwarded to the trigger `<button>` so an external `<label htmlFor>` can
   * associate with — and click-target — the control (e.g. AddressStep's
   * `<Label htmlFor='country'>`, #5311). Omit when an `aria-label` already names
   * the control (e.g. VatControls).
   */
  id?: string | undefined;
}

// A left-anchored dropdown reserves at least this many px to the right of the
// trigger's left edge (Tailwind `min-w-[220px]`). When the trigger sits near
// the right viewport edge (e.g. the 120px VatControls picker on mobile) that
// would spill off-screen, so we flip the anchor to the trigger's right edge.
const DROPDOWN_MIN_WIDTH_PX = 220;

// Diacritic- and case-insensitive normalization so "svaj" matches
// "Švajčiarsko" and "turkiye"/"turkey" both match "Türkiye".
export const normalizeCountryQuery = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/**
 * Match an option against a search query. Diacritic- and case-insensitive on
 * the label and any keywords; an exact (normalized) match on the ISO code also
 * hits. An empty query matches everything. Exported so the search contract is
 * unit-tested independently of the render.
 */
export const countryOptionMatches = (
  option: CountryComboboxOption,
  query: string
): boolean => {
  const q = normalizeCountryQuery(query);
  if (!q) return true;
  if (normalizeCountryQuery(option.label).includes(q)) return true;
  if (option.value.toLowerCase() === q) return true;
  return (
    option.keywords?.some((k) => normalizeCountryQuery(k).includes(q)) ?? false
  );
};

/**
 * Searchable, option-driven country picker — the typeahead replacement for a
 * flat `<Select>` once the list grew past comfortable scroll length (43 items
 * after #5295). Pure presentation: callers own localization, ordering, and the
 * option list; this component only renders the trigger, the filter input, and
 * the filtered listbox. Shared by `CountrySelect` (checkout) and the pricing
 * page `VatControls` so the two identical dropdowns stay in lockstep (#5298).
 *
 * Built on a controlled wrapper + conditionally-rendered listbox (the repo's
 * `CityCombobox` idiom) rather than a portal so positioning is wrapper-relative
 * and the open/filter/commit behaviour is exercisable in happy-dom.
 */
export function CountryCombobox({
  options,
  value,
  onValueChange,
  disabled = false,
  placeholder = 'Select country',
  searchPlaceholder = 'Search…',
  emptyText = 'No country found.',
  className,
  error = false,
  'aria-label': ariaLabel,
  id,
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  // Horizontal anchor: false → left-0 (default), true → right-0 when a
  // left-anchored panel would overflow the viewport (#5310). Recomputed on open.
  const [alignRight, setAlignRight] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const activeOptionRef = useRef<HTMLLIElement>(null);
  const reactId = useId();
  const listboxId = `country-combobox-${reactId}`;

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  const filtered = useMemo(
    () => options.filter((o) => countryOptionMatches(o, query)),
    [options, query]
  );

  // Close on outside click (mirrors CityCombobox).
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    // Land the active descendant on the currently-selected option rather than
    // always row 0 (ARIA APG combobox §3.8). query is reset to '' here so
    // `filtered` === `options` and the index aligns; fallback 0 when nothing is
    // selected or the value isn't in the list (#5309).
    const selectedIdx = options.findIndex((o) => o.value === value);
    setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    // Flip to a right-edge anchor when a left-anchored min-w-[220px] panel would
    // spill past the right viewport edge from a narrow trigger (#5310).
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      setAlignRight(rect.left + DROPDOWN_MIN_WIDTH_PX > window.innerWidth);
    }
    setOpen(true);
  };

  // Bring the active option into view when the menu opens or the active index
  // moves — without it the selected row picked above (or an arrow-key target)
  // can sit scrolled out of the 260px listbox. scrollIntoView is optional-chained
  // for environments (happy-dom) that don't implement it.
  useEffect(() => {
    if (!open) return;
    activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIdx]);

  const closeMenu = (focusTrigger = false) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const commit = (next: string) => {
    onValueChange(next);
    closeMenu(true);
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu(true);
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = filtered[activeIdx];
      if (picked) commit(picked.value);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className='relative'
      // Close when focus leaves the widget entirely (e.g. Tab past the search
      // input). relatedTarget is the element receiving focus; if it is still
      // inside the wrapper we stay open.
      onBlur={(e) => {
        if (open && !wrapperRef.current?.contains(e.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        id={id}
        type='button'
        role='combobox'
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup='listbox'
        aria-controls={open ? listboxId : undefined}
        aria-invalid={error || undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-status-error',
          className
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className='h-4 w-4 shrink-0 opacity-50'
          aria-hidden='true'
        />
      </button>

      {open && (
        <div
          className={cn(
            'absolute top-full z-50 mt-1 w-full min-w-[220px] max-w-[min(20rem,90vw)] overflow-hidden rounded-md border border-line-default bg-popover text-popover-foreground shadow-md',
            alignRight ? 'right-0' : 'left-0'
          )}
        >
          <div className='flex items-center border-b border-line-subtle px-3'>
            <Search
              className='h-4 w-4 shrink-0 text-content-muted'
              aria-hidden='true'
            />
            <input
              autoFocus
              type='text'
              aria-label={searchPlaceholder}
              aria-controls={listboxId}
              aria-activedescendant={
                filtered[activeIdx]
                  ? `${listboxId}-opt-${filtered[activeIdx].value}`
                  : undefined
              }
              autoComplete='off'
              spellCheck={false}
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onSearchKeyDown}
              className='flex h-10 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground'
            />
          </div>
          {filtered.length === 0 ? (
            <p className='px-3 py-4 text-center text-sm text-content-muted'>
              {emptyText}
            </p>
          ) : (
            <ul
              id={listboxId}
              role='listbox'
              className='max-h-[260px] overflow-y-auto p-1'
            >
              {filtered.map((o, idx) => {
                const isSelected = o.value === value;
                const isActive = idx === activeIdx;
                return (
                  <li
                    key={o.value}
                    ref={isActive ? activeOptionRef : undefined}
                    id={`${listboxId}-opt-${o.value}`}
                    role='option'
                    aria-selected={isSelected}
                    className={cn(
                      'flex min-h-11 cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-content-secondary'
                    )}
                    // onMouseDown so the pick lands before the input blur/close.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(o.value);
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <Check
                      aria-hidden='true'
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className='truncate'>{o.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
